import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { setUser, setToken } from '../../redux/auth/authSlice';
import { RootState } from '../../redux/store';
import {
  signInWithGoogle,
  sendMagicLink,
} from '../../services/firebase/firebaseService';
import tokenManager from '../../utils/tokenManager';
import {
  trackMetaCompleteRegistration,
  trackMetaSignupView,
} from '../../utils/metaPixel';
import { trackXSignUp } from '../../utils/xPixel';
import {
  trackOnce,
  getAnonymousId,
  ANALYTICS_EVENTS,
} from '../../utils/productAnalytics';
import { User } from '../../types';
import { getPostAuthDestination } from '../../utils/authDestination';
import Image from '../../components/common/Image';
import {
  TaskyAlert,
  TaskyAuthLayout,
  TaskyButton,
  TaskyFormGroup,
  TaskyInput,
} from '../../components/tasky';
import {
  PARTNER_REF_REGEX,
  PARTNER_REF_STORAGE_KEY,
  PARTNER_TRIAL_DAYS_STORAGE_KEY,
  parsePartnerTrialDays,
} from '../../constants/partnerRef';
import { INVITE_REF_REGEX, INVITE_REF_STORAGE_KEY } from '../../constants/inviteRef';
import InviterBanner from '../../components/invite/InviterBanner';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SignupPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    isAuthenticated,
    user,
    error: authError,
  } = useSelector((state: RootState) => state.auth);

  // profile API 永続失敗の検知:
  // isAuthenticated は localStorage の token/refresh_token だけで true になる
  // が、サーバー側不調などで getUserProfile が rejected され続けると user が
  // null のまま固まる。前段の useEffect は `if (!user) return;` でループを
  // 抜けるので画面遷移しなくなり、ユーザーは SignupPage で「ログイン済みなのに
  // 何も起きない」状態になる。
  //
  // この状態を検知してエラー UI と「ログアウトしてやり直す」ボタンを出し、
  // ユーザーが手動でセッションをリセットできるようにする。
  const isProfileLoadStuck = isAuthenticated && !user && Boolean(authError);

  const [showEmailForm, setShowEmailForm] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [generalError, setGeneralError] = useState('');
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isEmailSubmitting, setIsEmailSubmitting] = useState(false);
  // 友だち紹介URL経由なら「誰の招待で来たか」を出すためのコード。
  const [inviteRef, setInviteRef] = useState<string | null>(null);

  useEffect(() => {
    trackMetaSignupView();
    trackOnce(ANALYTICS_EVENTS.SIGNUP_STARTED);
  }, []);

  // 招待URL(?partner_ref=CODE)経由のパートナー帰属コードを保存する。
  // マジックリンクや Google 認証の往復で URL が失われても、オンボーディング
  // 完了時に送れるよう localStorage に退避しておく。
  //
  // 延長トライアル用の招待URL(&trial_days=14)なら日数も併せて退避する。
  // 帰属コードとセットでしか意味を持たない値なので、partner_ref が無い
  // (または不正な) URL では保存せず、古い値が残っていれば破棄する。
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('partner_ref');
    if (ref && PARTNER_REF_REGEX.test(ref)) {
      localStorage.setItem(PARTNER_REF_STORAGE_KEY, ref);
      const trialDays = parsePartnerTrialDays(params.get('trial_days'));
      if (trialDays) {
        localStorage.setItem(
          PARTNER_TRIAL_DAYS_STORAGE_KEY,
          String(trialDays),
        );
      } else {
        localStorage.removeItem(PARTNER_TRIAL_DAYS_STORAGE_KEY);
      }
    }
  }, []);

  // 友だち紹介URL(?invite_ref=CODE)経由の帰属コード。partner_ref と同じ理由で
  // localStorage に退避する（マジックリンク・Google 認証の往復で URL が失われる）。
  //
  // partner_ref と違い、invite_ref が無い URL では古い値を必ず破棄する。
  // 残したままだと、別経路で登録した人まで前回の招待者に紐づいてしまう。
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get('invite_ref');

    if (ref && INVITE_REF_REGEX.test(ref)) {
      localStorage.setItem(INVITE_REF_STORAGE_KEY, ref);
      setInviteRef(ref);
      return;
    }

    localStorage.removeItem(INVITE_REF_STORAGE_KEY);
    setInviteRef(null);
  }, []);

  // ログイン済みユーザーが /signup を訪れたら自動で次の画面に遷移する。
  // 以前は無条件で /home に飛ばしていたが、Google で新規登録した直後
  // (organization_id 未付与) のユーザーまで /home に飛ばされ、会社情報
  // 入力画面 (/signup/setup) に到達できずオンボーディングが始まらない
  // 不具合があった (handleGoogleSignIn の navigate と競合)。
  // organization 有無と onboarding_completed を分けて判定し、組織作成後も
  // 未完了なら必ず /signup/onboarding を通すことで競合を解消する。
  //
  // また、authSlice の isAuthenticated は localStorage の token/refresh_token
  // だけで true に初期化されるため、リロード直後で user プロフィール復元前の
  // 既存ユーザーが /signup を踏むと user は null のまま isAuthenticated=true
  // という過渡状態が発生する。この間 organization_id を「未付与」と判定して
  // /signup/setup に送ると組織所属の既存ユーザーまで誤って setup 画面に飛ば
  // されるので、user が null の間は遷移を保留する。
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!user) return;
    navigate(getPostAuthDestination(user));
  }, [isAuthenticated, user, navigate]);

  const handleForceLogout = () => {
    // profile API 永続失敗状態からの脱出口。
    //
    // 設計上の要件:
    //   a. クリック時点で確実にローカル認証情報を破棄する (API ハングしても stuck しない)
    //   b. backend 側の personal_access_tokens / refresh_tokens も可能な限り失効させる
    //
    // 注意点:
    //   - axios の timeout は 600 秒 (axiosConfig.ts:29) なので remote logout を
    //     await するのは NG (要件 a を満たせない)。
    //   - 一方、先に clearAllAuthData() → authService.logout() の順だと axios
    //     interceptor が消えた token を読み Authorization なしで /auth/logout に
    //     ヒットして 401 になり、要件 b を満たせない。
    //
    // 解決: token を退避 → ローカル clear → 退避 token で明示的に fetch を
    // `keepalive: true` で送る (ページ unload 後もブラウザがリクエストを継続)。
    const accessToken = tokenManager.getToken();
    tokenManager.clearAllAuthData();

    if (accessToken) {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const base = apiUrl.endsWith('/api') ? apiUrl : `${apiUrl}/api`;
      try {
        void fetch(`${base}/auth/logout`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          // navigation 後もリクエストを継続させる。axios の interceptor を
          // 経由しないので、消した token に依存することもない。
          keepalive: true,
        }).catch(() => {
          // ネットワーク不調なら無視。escape hatch としての要件はローカル clear
          // で既に達成済み。
        });
      } catch {
        // fetch そのものが throw した場合 (環境依存) も無視。
      }
    }

    // リロードで Redux state を確実に初期化 (token なし → isAuthenticated=false)。
    window.location.href = '/signup';
  };

  const handleGoogleSignIn = async () => {
    setGeneralError('');
    setIsGoogleLoading(true);
    try {
      const response = await signInWithGoogle();
      if (response?.token) dispatch(setToken(response.token));
      if (response?.user) dispatch(setUser(response.user as User));

      const orgId = response?.user?.organization_id;
      if (!orgId) {
        trackMetaCompleteRegistration(response?.user as User | undefined);
        trackXSignUp(response?.user as User | undefined);
        // BI: signup_completed（Google 新規登録。org 未確定 = 新規ユーザー）。
        // signInWithGoogle 内で token は localStorage 保存済み → user_id が確定する。
        trackOnce(ANALYTICS_EVENTS.SIGNUP_COMPLETED, {
          anonymous_id: getAnonymousId(),
          method: 'google',
        });
      }
      navigate(getPostAuthDestination(response?.user as User | undefined));
    } catch (err) {
      console.error('Google sign-in failed:', err);
      setGeneralError(
        'Google ログインに失敗しました。もう一度お試しください。',
      );
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleSendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setNameError('');
    setEmailError('');
    setGeneralError('');

    const trimmedName = name.trim();
    const trimmed = email.trim();
    if (!trimmedName) {
      setNameError('お名前を入力してください');
      return;
    }

    if (!trimmed || !EMAIL_REGEX.test(trimmed)) {
      setEmailError('メールアドレスの形式が正しくありません');
      return;
    }

    setIsEmailSubmitting(true);
    try {
      await sendMagicLink(trimmed, trimmedName);
      navigate('/auth/magic-link-sent');
    } catch (err) {
      console.error('Send magic link failed:', err);
      setGeneralError(
        'メールの送信に失敗しました。しばらく時間をおいて再度お試しください。',
      );
    } finally {
      setIsEmailSubmitting(false);
    }
  };

  return (
    <TaskyAuthLayout
      logo={
        <Image category="common" fileName="logo.png" alt="Tasky" width={100} />
      }
      title="まずは無料で試す"
      description="7日間すべての機能を無料でお試しいただけます。"
      footer={
        <>
          <div>
            すでにアカウントをお持ちの方は{' '}
            <Link
              to="/login"
              className="text-primary hover:text-primary-hover font-medium"
            >
              ログイン
            </Link>
          </div>
          <div className="mt-3 text-xs text-gray-400 leading-relaxed">
            登録することで、
            <a
              href="https://lp.tasky-ai.com/legal/terms.html"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-gray-600"
            >
              利用規約
            </a>
            と
            <a
              href="https://lp.tasky-ai.com/legal/privacy.html"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-gray-600"
            >
              プライバシーポリシー
            </a>
            に同意したものとみなされます。
          </div>
        </>
      }
    >
      {/* 友だち紹介リンクから来た人に「誰の招待か」を見せる。専用の招待LPは作らない。 */}
      <InviterBanner code={inviteRef} />

      {generalError && (
        <TaskyAlert tone="error" className="mb-4">
          {generalError}
        </TaskyAlert>
      )}

      {isProfileLoadStuck && (
        <TaskyAlert tone="error" className="mb-4">
          <div>
            ログイン情報の復元に失敗しました{authError ? `: ${authError}` : ''}
            。
            <br />
            一度ログアウトしてから再度お試しください。
          </div>
          <TaskyButton
            type="button"
            variant="outline"
            onClick={handleForceLogout}
            className="mt-3"
          >
            ログアウトしてやり直す
          </TaskyButton>
        </TaskyAlert>
      )}

      {!showEmailForm ? (
        <div className="space-y-3">
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={isGoogleLoading}
            className="w-full bg-white hover:bg-warm-hover border border-warm-border rounded-tasky-sm py-2.5 text-sm font-medium text-gray-700 flex items-center justify-center gap-2.5 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <img
              src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
              alt="Google"
              width={20}
              height={20}
              className="shrink-0"
            />
            <span>{isGoogleLoading ? '処理中...' : 'Google で続ける'}</span>
          </button>

          <TaskyButton
            type="button"
            variant="outline"
            onClick={() => setShowEmailForm(true)}
            className="w-full"
          >
            メールで続ける
          </TaskyButton>
        </div>
      ) : (
        <form onSubmit={handleSendMagicLink} className="space-y-4">
          <TaskyFormGroup
            label="お名前"
            required
            htmlFor="signup-name"
            error={nameError}
          >
            <TaskyInput
              id="signup-name"
              type="text"
              placeholder="山田 太郎"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError('');
              }}
              hasError={Boolean(nameError)}
            />
          </TaskyFormGroup>

          <TaskyFormGroup
            label="メールアドレス"
            required
            htmlFor="signup-email"
            error={emailError}
          >
            <TaskyInput
              id="signup-email"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError('');
              }}
              hasError={Boolean(emailError)}
            />
          </TaskyFormGroup>

          <TaskyButton
            type="submit"
            isLoading={isEmailSubmitting}
            className="w-full"
          >
            {isEmailSubmitting ? '送信中...' : '送信'}
          </TaskyButton>

          <TaskyButton
            type="button"
            variant="outline"
            onClick={() => {
              setShowEmailForm(false);
              setNameError('');
              setEmailError('');
            }}
            className="w-full"
          >
            戻る
          </TaskyButton>
        </form>
      )}
    </TaskyAuthLayout>
  );
};

export default SignupPage;
